import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { useLanguage } from "@/i18n";
import { scrollToId } from "@/components/Reveal";

const SLIDES = [
    { src: "/assets/porcini.jpg", alt: "Funghi porcini", pos: "center 22%" },
    { src: "/assets/hero-truffle.jpg", alt: "Tartufo fresco", pos: "center 40%" },
    { src: "/assets/ittico.jpg", alt: "Gambero rosso", pos: "center 45%" },
    { src: "/assets/tbone.png", alt: "Carne grass-fed", pos: "center center" },
];

export const Hero = () => {
    const { t } = useLanguage();
    const ref = useRef(null);
    const [slide, setSlide] = useState(0);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const yBg = useTransform(scrollYProgress, [0, 1], [0, 220]);
    const scaleBg = useTransform(scrollYProgress, [0, 1], [1.08, 1.22]);
    const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

    useEffect(() => {
        const id = setInterval(() => setSlide((s) => (s + 1) % SLIDES.length), 6000);
        return () => clearInterval(id);
    }, []);

    const lines = t("hero.lines");

    return (
        <section id="hero" data-testid="hero-section" ref={ref} className="relative flex min-h-[100svh] items-end overflow-hidden">
            <motion.div style={{ y: yBg, scale: scaleBg }} className="absolute inset-0">
                <AnimatePresence mode="sync">
                    <motion.img
                        key={slide}
                        src={SLIDES[slide].src}
                        alt={SLIDES[slide].alt}
                        data-testid="hero-slide-image"
                        initial={{ opacity: 0, scale: 1.05 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 1.6, ease: "easeInOut" }}
                        style={{ objectPosition: SLIDES[slide].pos }}
                        className="absolute inset-0 h-full w-full object-cover"
                    />
                </AnimatePresence>
            </motion.div>
            <div className="absolute inset-0 bg-gradient-to-t from-[#1C2029] via-[#1C2029]/55 to-[#1C2029]/30" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#1C2029]/85 via-[#1C2029]/30 to-transparent" />
            <div className="absolute bottom-8 right-20 z-10 flex items-center gap-2" data-testid="hero-slide-dots">
                {SLIDES.map((s, i) => (
                    <button
                        key={s.src}
                        data-testid={`hero-slide-dot-${i}`}
                        onClick={() => setSlide(i)}
                        aria-label={s.alt}
                        className={`h-1 transition-all duration-500 ${i === slide ? "w-10 bg-[#DBD1BD]" : "w-4 bg-white/25 hover:bg-white/50"}`}
                    />
                ))}
            </div>

            <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-[1600px] px-5 pb-24 pt-40 md:px-12 md:pb-32 lg:px-24">
                <motion.p
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1, duration: 0.7 }}
                    className="mb-6 font-mono text-xs uppercase tracking-[0.3em] text-white md:text-sm"
                    data-testid="hero-overline"
                >
                    {t("hero.overline")}
                </motion.p>

                <h1 className="font-display text-5xl font-light leading-[0.95] tracking-tighter sm:text-7xl lg:text-8xl" data-testid="hero-title">
                    {lines.map((line, i) => (
                        <span key={i} className="block overflow-hidden pb-1">
                            <motion.span
                                className="block"
                                initial={{ y: "110%" }}
                                animate={{ y: 0 }}
                                transition={{ delay: 0.25 + i * 0.16, duration: 1, ease: [0.22, 1, 0.36, 1] }}
                            >
                                {i === 1 ? <em className="font-light italic text-midora-red">{line}</em> : line}
                            </motion.span>
                        </span>
                    ))}
                </h1>

                <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.9, duration: 0.8 }}
                    className="mt-8 max-w-xl text-base italic text-white md:text-lg"
                    data-testid="hero-subtitle"
                >
                    {t("hero.sub")}
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.05, duration: 0.7 }}
                    className="mt-10 flex flex-col gap-4 sm:flex-row"
                >
                    <button
                        data-testid="hero-cta-products"
                        onClick={() => scrollToId("prodotti")}
                        className="bg-[#DBD1BD] px-8 py-4 font-mono text-[11px] uppercase tracking-[0.2em] text-[#1C2029] transition-colors duration-300 hover:bg-midora-red hover:text-white"
                    >
                        {t("hero.ctaPrimary")}
                    </button>
                    <button
                        data-testid="hero-cta-listino"
                        onClick={() => scrollToId("contatti")}
                        className="border border-white/25 px-8 py-4 font-mono text-[11px] uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:border-midora-red hover:text-midora-red"
                    >
                        {t("hero.ctaSecondary")}
                    </button>
                </motion.div>
            </motion.div>

            <motion.button
                data-testid="hero-scroll-indicator"
                onClick={() => scrollToId("chi-siamo")}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.6 }}
                className="absolute bottom-8 right-6 z-10 hidden text-white/40 transition-colors duration-300 hover:text-white md:block"
                aria-label="Scroll"
            >
                <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }} className="block">
                    <ArrowDown className="h-5 w-5" />
                </motion.span>
            </motion.button>
        </section>
    );
};
